import AvatarFallback from "./AvatarFallback";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import React from "react";
import i18n from "i18";
import { Box, Typography } from "@mui/material";
import FavoriteIcon from "@mui/icons-material/Favorite";
import { ReactComponent as InstSave } from "../../images/instSave.svg";
import { ReactComponent as InstSend } from "../../images/instSend.svg";
import { MoreHorizontalIcon } from "../AdaptiveIcon";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

const sliderSettings = (numImages) => ({
  dots: numImages > 1,
  infinite: false,
  speed: 300,
  slidesToShow: 1,
  slidesToScroll: 1,
  arrows: false,
  customPaging: () => (
    <Box
      sx={{
        width: "6px",
        height: "6px",
        borderRadius: "50%",
        backgroundColor: "rgba(255, 255, 255, 0.5)",
        transition: "all 0.3s ease",
        "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.8)" },
      }}
    />
  ),
  dotsClass: "slick-dots instagram-dots",
  appendDots: (dots) => (
    <Box
      sx={{
        position: "absolute",
        bottom: "12px",
        width: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}>
      <ul style={{ margin: "0", padding: "0", display: "flex", gap: "6px" }}>
        {dots}
      </ul>
    </Box>
  ),
});

function AccountHeader({ selectedAccounts, accResp, avatars, selectedIndex }) {
  return (
    <Box
      className="instHeader mt-4"
      sx={{
        height: "20px",
        position: "relative",
        marginBottom: selectedAccounts.length === 0 ? "0px" : "30px",
      }}>
      {selectedAccounts.length === 0 ? (
        <Box
          className="avatar"
          sx={{
            width: "50px",
            height: "50px",
            borderRadius: "50%",
            backgroundColor: "var(--background-preview)",
            position: "absolute",
          }}
        />
      ) : (
        <>
          {selectedAccounts.map((id, i) => {
            const index = accResp.findIndex((acc) => acc.id === id);
            const avatar = avatars[index];
            const userName = accResp[index].username;
            return (
              <Box
                key={id}
                className="author-img"
                sx={{
                  backgroundColor: "var(--background-preview)",
                  position: "absolute",
                  display: i === selectedIndex ? "flex" : "none",
                  alignItems: "center",
                }}>
                <Box sx={{
                  width: "44px",
                  height: "44px",
                  borderRadius: "50%",
                  padding: "2px",
                  background: "linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)",
                  flexShrink: 0,
                }}>
                  <AvatarFallback
                    src={avatar}
                    name={userName}
                    background="#c4c7cc"
                    size="100%"
                    fontSize="16px"
                    style={{ borderRadius: "50%", width: "100%", height: "100%", objectFit: "cover", display: "block", border: "2px solid var(--background-menu)" }}
                    fallbackStyle={{ border: "2px solid var(--background-menu)" }}
                  />
                </Box>
                <Box className="flex column" style={{ marginLeft: "10px" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <Typography className="whitespace-nowrap" sx={{ color: "var(--text-color)", fontWeight: 600, fontSize: "14px" }}>
                      {userName}
                    </Typography>
                    <Typography className="whitespace-nowrap" sx={{ color: "#1d9bf0", fontWeight: 600, fontSize: "14px", cursor: "pointer" }}>
                      Follow
                    </Typography>
                  </Box>
                  <Typography className="whitespace-nowrap" sx={{ color: "#a9a9a9", fontSize: "12px" }}>
                    1d
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </>
      )}
      <Box className="absolute right-0">
        <MoreHorizontalIcon fontSize="large" />
      </Box>
    </Box>
  );
}

function MediaBox({ selectedImages, width, height, borderRadius = "10px" }) {
  const numImages = selectedImages.length;
  return (
    <Box sx={{ width, height, position: "relative", flexShrink: 0 }}>
      {numImages === 0 ? (
        // Instagram cannot publish without a file, so this state is what every
        // user stares at while composing. A bare 523x523 void said nothing;
        // say what is missing instead (OP#2507).
        <Box
          sx={{
            width: "100%",
            height: "100%",
            backgroundColor: "var(--background-preview)",
            borderRadius,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            color: "var(--text-color)",
            opacity: 0.6,
            textAlign: "center",
            padding: "16px",
          }}>
          <AddPhotoAlternateOutlinedIcon sx={{ fontSize: 40 }} />
          <Typography sx={{ fontSize: "13px", maxWidth: "220px" }}>
            {i18n.t("postPage.previewNeedsImage")}
          </Typography>
        </Box>
      ) : numImages === 1 ? (
        <img
          src={selectedImages[0].props.src}
          alt=""
          style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius }}
        />
      ) : (
        <Slider {...sliderSettings(numImages)}>
          {selectedImages.map((image, index) => (
            <Box key={index} sx={{ width, height }}>
              <img
                src={image.props.src}
                alt={`Slide ${index + 1}`}
                style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius, display: "block" }}
              />
            </Box>
          ))}
        </Slider>
      )}
    </Box>
  );
}

// ── Feed preview (square, carousel, caption) ──────────────────────────────────
function FeedPreview({ selectedAccounts, accResp, avatars, selectedIndex, selectedImages, postText }) {
  return (
    <Box className="flex justify-center p-2">
      <Box className="instContainer">
        <AccountHeader
          selectedAccounts={selectedAccounts}
          accResp={accResp}
          avatars={avatars}
          selectedIndex={selectedIndex}
        />
        <Box
          className="user-info"
          style={{ position: "relative", paddingLeft: "60px", top: "-25px" }}>
          {selectedAccounts.length === 0 && (
            <>
              <Box style={{ width: "30%", height: "12px", backgroundColor: "var(--background-preview)", marginBottom: "5px" }} />
              <Box style={{ width: "10%", height: "12px", backgroundColor: "var(--background-preview)", marginBottom: "5px" }} />
            </>
          )}
        </Box>

        <Box className="flex justify-center">
          <MediaBox selectedImages={selectedImages} width="523px" height="523px" />
        </Box>

        <Box className="flex items-center relative interactions gap-6 mb-2">
          <FavoriteIcon className="heart" />
          <svg className="comment" aria-label="Comment" fill="none" stroke="currentColor" strokeLinejoin="round" strokeWidth="2" height="24" role="img" viewBox="0 0 24 24" width="24">
            <title>Comment</title>
            <path d="M20.656 17.008a9.993 9.993 0 1 0-3.59 3.615L22 22Z"></path>
          </svg>
          <InstSend width={24} height={24} />
          <InstSave className="absolute right-0" width={24} height={24} />
        </Box>
        <Box className="mb-2">
          <Typography component="span" className="like-count"><b>1,247 likes</b></Typography>
        </Box>
        {selectedAccounts && selectedAccounts.length > 0 ? (
          selectedAccounts.map((id, i) => {
            const index = accResp.findIndex((acc) => acc.id === id);
            const userName = accResp[index]?.username || "Unknown User";
            return (
              <Box key={i} className="flex mb-2" style={{ flexDirection: "column" }}>
                <Typography component="div" style={{ color: "var(--text-color)", fontSize: "14px", lineHeight: "1.5" }}>
                  <b style={{ marginRight: "6px" }}>{userName}</b>
                  <span style={{ whiteSpace: "pre-wrap" }}>{postText}</span>
                </Typography>
              </Box>
            );
          })
        ) : (
          <Box className="mb-2">
            <Box style={{ width: "100%", height: "12px", backgroundColor: "var(--background-preview)", marginBottom: "5px" }} />
            <Box style={{ width: "60%", height: "12px", backgroundColor: "var(--background-preview)", marginBottom: "5px" }} />
          </Box>
        )}
        <Box className="instHeader">
          <input className="inputField" id="user-comment" type="text" placeholder="Add comment..." />
          <Box>
            <svg aria-label="Share Post" fill="currentColor" height="24" role="img" viewBox="0 0 24 24" width="24">
              <title>Share Post</title>
              <line fill="none" stroke="currentColor" strokeLinejoin="round" strokeWidth="2" x1="22" x2="9.218" y1="3" y2="10.083"></line>
              <polygon fill="none" points="11.698 20.334 22 3.001 2 3.001 9.218 10.084 11.698 20.334" stroke="currentColor" strokeLinejoin="round" strokeWidth="2"></polygon>
            </svg>
          </Box>
        </Box>
        <Typography sx={{ color: "#a9a9a9", fontSize: "13px", marginBottom: "8px", cursor: "pointer" }}>View all 14 comments</Typography>
        <Typography sx={{ color: "#a9a9a9", fontSize: "12px" }}>1 DAY AGO</Typography>
        <Box id="custom-comment"></Box>
      </Box>
    </Box>
  );
}

// ── Reel preview (9:16 vertical, badge, caption overlay) ─────────────────────
function ReelPreview({ selectedAccounts, accResp, avatars, selectedIndex, selectedImages, postText }) {
  const userName = selectedAccounts.length > 0
    ? (accResp.find((acc) => acc.id === selectedAccounts[selectedIndex]) || accResp.find((acc) => selectedAccounts.includes(acc.id)))?.username || ""
    : "";
  const avatar = selectedAccounts.length > 0
    ? avatars[accResp.findIndex((acc) => acc.id === (selectedAccounts[selectedIndex] || selectedAccounts[0]))]
    : null;

  return (
    <Box className="flex justify-center p-2">
      <Box sx={{ width: "300px", position: "relative" }}>
        {/* Vertical media area */}
        <Box sx={{ width: "300px", height: "533px", position: "relative", borderRadius: "12px", overflow: "hidden", backgroundColor: "var(--background-preview)" }}>
          {selectedImages.length > 0 && (
            <img
              src={selectedImages[0].props.src}
              alt=""
              style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
            />
          )}

          {/* Reel badge */}
          <Box sx={{
            position: "absolute",
            top: "12px",
            left: "12px",
            display: "flex",
            alignItems: "center",
            gap: "5px",
            backgroundColor: "rgba(0,0,0,0.45)",
            borderRadius: "12px",
            padding: "3px 10px",
          }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="white">
              <path d="M2 3h20v18H2zm9 5v8l7-4z"/>
            </svg>
            <Typography sx={{ color: "#fff", fontSize: "11px", fontWeight: 700, letterSpacing: "0.5px" }}>Reel</Typography>
          </Box>

          {/* Bottom overlay: author + caption + actions */}
          <Box sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            background: "linear-gradient(transparent, rgba(0,0,0,0.65))",
            padding: "32px 12px 16px",
          }}>
            {/* Author */}
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px", mb: "8px" }}>
              {avatar ? (
                <img src={avatar} alt="" style={{ width: "32px", height: "32px", borderRadius: "50%", border: "2px solid white", objectFit: "cover" }} />
              ) : (
                <Box sx={{ width: "32px", height: "32px", borderRadius: "50%", border: "2px solid white", backgroundColor: "#c4c7cc" }} />
              )}
              <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "13px" }}>{userName || "username"}</Typography>
              <Box sx={{ border: "1px solid rgba(255,255,255,0.7)", borderRadius: "6px", padding: "2px 8px" }}>
                <Typography sx={{ color: "#fff", fontSize: "11px", fontWeight: 600 }}>Follow</Typography>
              </Box>
            </Box>
            {/* Caption */}
            {postText ? (
              <Typography sx={{ color: "#fff", fontSize: "12px", lineHeight: 1.45, opacity: 0.9, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
                {postText}
              </Typography>
            ) : null}
          </Box>

          {/* Right-side action icons */}
          <Box sx={{ position: "absolute", right: "10px", bottom: "80px", display: "flex", flexDirection: "column", alignItems: "center", gap: "18px" }}>
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "4px" }}>
              <FavoriteIcon sx={{ color: "white", fontSize: 26 }} />
              <Typography sx={{ color: "white", fontSize: "11px" }}>1.2K</Typography>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "4px" }}>
              <svg fill="white" height="24" width="24" viewBox="0 0 24 24"><path d="M20.656 17.008a9.993 9.993 0 1 0-3.59 3.615L22 22Z"></path></svg>
              <Typography sx={{ color: "white", fontSize: "11px" }}>48</Typography>
            </Box>
            <InstSend width={22} height={22} style={{ filter: "invert(1)" }} />
            <MoreHorizontalIcon sx={{ color: "white", fontSize: 24 }} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

// ── Story preview (9:16 vertical, no caption, progress bar) ───────────────────
function StoryPreview({ selectedAccounts, accResp, avatars, selectedIndex, selectedImages }) {
  const userName = selectedAccounts.length > 0
    ? (accResp.find((acc) => acc.id === selectedAccounts[selectedIndex]) || accResp.find((acc) => selectedAccounts.includes(acc.id)))?.username || ""
    : "";
  const avatar = selectedAccounts.length > 0
    ? avatars[accResp.findIndex((acc) => acc.id === (selectedAccounts[selectedIndex] || selectedAccounts[0]))]
    : null;

  return (
    <Box className="flex justify-center p-2">
      <Box sx={{ width: "300px", position: "relative" }}>
        <Box sx={{ width: "300px", height: "533px", position: "relative", borderRadius: "12px", overflow: "hidden", backgroundColor: "#111" }}>
          {selectedImages.length > 0 && (
            <img
              src={selectedImages[0].props.src}
              alt=""
              style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
            />
          )}

          {/* Progress bar */}
          <Box sx={{ position: "absolute", top: "10px", left: "10px", right: "10px" }}>
            <Box sx={{ height: "2px", borderRadius: "2px", backgroundColor: "rgba(255,255,255,0.35)", overflow: "hidden" }}>
              <Box sx={{ height: "100%", width: "40%", backgroundColor: "white", borderRadius: "2px" }} />
            </Box>
          </Box>

          {/* Top bar: avatar + name + time + close */}
          <Box sx={{ position: "absolute", top: "20px", left: "10px", right: "10px", display: "flex", alignItems: "center", gap: "8px" }}>
            <Box sx={{
              width: "36px", height: "36px", borderRadius: "50%", flexShrink: 0,
              background: "linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)",
              padding: "2px",
            }}>
              {avatar ? (
                <img src={avatar} alt="" style={{ width: "100%", height: "100%", borderRadius: "50%", objectFit: "cover", border: "2px solid #111", display: "block" }} />
              ) : (
                <Box sx={{ width: "100%", height: "100%", borderRadius: "50%", backgroundColor: "#c4c7cc" }} />
              )}
            </Box>
            <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "13px", flex: 1 }}>{userName || "username"}</Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.7)", fontSize: "11px" }}>1d</Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.7)", fontSize: "18px", lineHeight: 1, cursor: "pointer" }}>✕</Typography>
          </Box>

          {/* No caption note */}
          {selectedImages.length === 0 && (
            <Box sx={{ position: "absolute", bottom: "30px", left: 0, right: 0, textAlign: "center" }}>
              <Typography sx={{ color: "rgba(255,255,255,0.5)", fontSize: "12px" }}>No media added</Typography>
            </Box>
          )}

          {/* Bottom action row */}
          <Box sx={{ position: "absolute", bottom: "14px", left: "10px", right: "10px", display: "flex", alignItems: "center", gap: "8px" }}>
            <Box sx={{ flex: 1, height: "36px", borderRadius: "20px", border: "1px solid rgba(255,255,255,0.5)", display: "flex", alignItems: "center", paddingLeft: "14px" }}>
              <Typography sx={{ color: "rgba(255,255,255,0.6)", fontSize: "12px" }}>Send message</Typography>
            </Box>
            <FavoriteIcon sx={{ color: "white", fontSize: 24 }} />
            <InstSend width={22} height={22} style={{ filter: "invert(1)" }} />
          </Box>
        </Box>

        {/* Caption ignored note */}
        <Box sx={{ display: "flex", alignItems: "center", gap: "5px", mt: "8px", px: "4px" }}>
          <Typography sx={{ fontSize: "11px", color: "var(--text-color)", opacity: 0.4, lineHeight: 1.4 }}>
            Caption is ignored by Instagram for Stories.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

const Instagram = ({
  selectedAccounts,
  formattedTags,
  accResp,
  selectedImages,
  selectedIndex,
  avatars,
  postTitle,
  postText,
  postType = "feed",
}) => {
  if (postType === "reel") {
    return (
      <ReelPreview
        selectedAccounts={selectedAccounts}
        accResp={accResp}
        avatars={avatars}
        selectedIndex={selectedIndex}
        selectedImages={selectedImages}
        postText={postText}
      />
    );
  }

  if (postType === "story") {
    return (
      <StoryPreview
        selectedAccounts={selectedAccounts}
        accResp={accResp}
        avatars={avatars}
        selectedIndex={selectedIndex}
        selectedImages={selectedImages}
      />
    );
  }

  return (
    <FeedPreview
      selectedAccounts={selectedAccounts}
      accResp={accResp}
      avatars={avatars}
      selectedIndex={selectedIndex}
      selectedImages={selectedImages}
      postText={postText}
    />
  );
};

export default Instagram;
